import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Switch,
    Select,
    AutoComplete,
    message,
    Tooltip,
    Divider,
    Alert,
    Popconfirm,
    Collapse,
    Descriptions,
    Typography,
    InputNumber,
    Dropdown,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    SyncOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloudServerOutlined,
    DatabaseOutlined,
    WifiOutlined,
    DisconnectOutlined,
    ExclamationCircleOutlined,
    ApartmentOutlined,
    KeyOutlined,
    CopyOutlined,
    GlobalOutlined,
    MoreOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import NodeIpsPanel from './NodeIpsPanel';
import H3Suggestions from './H3Suggestions';

const { Option } = Select;
const { Text, Paragraph } = Typography;

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '-');

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
        document.body.removeChild(ta);
        return ok;
    }
};

// BGP 状态(后端 BgpStatus::classify)
const BGP_META = {
    announcing: { color: 'success', text: '宣告中' },
    withdrawn: { color: 'warning', text: '已撤回' },
    session_down: { color: 'error', text: '会话断开' },
    bird_down: { color: 'error', text: 'BIRD 未运行' },
    no_report: { color: 'default', text: '未上报' },
};
const bgpSessions = (b) => (b?.sessions || []).filter(x => x.proto === 'BGP');
const bgpTip = (b) => {
    const lines = bgpSessions(b).map(x => `${x.name}: ${x.info || x.state} · 导出 ${x.exported} · ${x.since}`);
    if (b?.config_synced === false) lines.push(`配置未同步(节点 ${b.version || '-'} / 控制面 ${b.expected_version})`);
    if (b?.reported_at) lines.push(`上报于 ${fmtTime(b.reported_at * 1000)}`);
    return lines.length ? lines.map((l, i) => <div key={i}>{l}</div>) : '尚未收到 yeap-agent 上报';
};

const Nodes = () => {
    const [nodes, setNodes] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [searchParams] = useSearchParams();
    const [keyword, setKeyword] = useState(() => searchParams.get('search') || '');
    const [wsFilter, setWsFilter] = useState('all');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingNode, setEditingNode] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [showInactive, setShowInactive] = useState(false);
    const [nodeGroups, setNodeGroups] = useState([]);
    const [form] = Form.useForm();

    // 站点版本弹窗
    const [versionsModalVisible, setVersionsModalVisible] = useState(false);
    const [versionsLoading, setVersionsLoading] = useState(false);
    const [versionsData, setVersionsData] = useState(null);
    const [versionsFilter, setVersionsFilter] = useState(null);

    // 节点状态弹窗
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const [statusNode, setStatusNode] = useState(null);

    // 密钥弹窗 { node, env (明文 edge.env 片段, 仅轮换/新建后展示), rotating }
    const [keyModal, setKeyModal] = useState(null);
    const [ipsNode, setIpsNode] = useState(null);

    const [healthMap, setHealthMap] = useState({});
    const [togglingId, setTogglingId] = useState(null);
    const [reloadingId, setReloadingId] = useState(null);

    useEffect(() => {
        loadNodes();
        loadNodeGroups();
    }, []);

    const [h3Open, setH3Open] = useState(false);

    const loadNodes = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await cdnNodeOpsService.getNodes();
            if (response && response.success) {
                setNodes(Array.isArray(response.data) ? response.data : []);
                setRegions(Array.isArray(response.regions) ? response.regions : []);
            } else {
                setLoadError(response?.error || '加载节点失败');
            }
        } catch (e) {
            setLoadError(e?.message || '加载节点失败');
        } finally {
            setLoading(false);
        }
    };

    const loadNodeGroups = async () => {
        const response = await cdnService.getNodeGroups();
        if (response && response.success) {
            setNodeGroups(Array.isArray(response.data) ? response.data : []);
        }
    };

    const filteredNodes = useMemo(() => {
        let list = nodes;
        if (!showInactive) list = list.filter(n => n.status !== 'inactive');
        if (selectedRegion !== 'all') list = list.filter(n => n.region === selectedRegion);
        if (selectedGroup === 'none') {
            list = list.filter(n => !n.node_groups || n.node_groups.length === 0);
        } else if (selectedGroup !== 'all') {
            const gid = parseInt(selectedGroup, 10);
            list = list.filter(n => n.node_groups && n.node_groups.some(g => g.id === gid));
        }
        if (wsFilter !== 'all') list = list.filter(n => (wsFilter === 'online') === !!n.ws?.online);
        const kw = keyword.trim().toLowerCase();
        if (kw) {
            list = list.filter(n => [n.name, n.hostname, n.ip, n.ipv4, n.ipv6, n.internal_ip, n.vip, n.colo, String(n.id)]
                .some(v => v && String(v).toLowerCase().includes(kw)));
        }
        return list;
    }, [nodes, showInactive, selectedRegion, selectedGroup, wsFilter, keyword]);

    const inactiveCount = nodes.filter(n => n.status === 'inactive').length;

    // ───────── 增 / 改 / 删 ─────────
    const handleAdd = () => {
        setEditingNode(null);
        form.resetFields();
        form.setFieldsValue({ sync_port: 9001, status: true, is_l2: false, is_accel: false, block_direct_access: false, edge_type: 'rust', rate_l1: 1, rate_l2: 1, rate_l3: 1, bgp_capable: false, h3_exclude: [] });
        setModalVisible(true);
    };

    const parseH3Exclude = (v) => {
        if (Array.isArray(v)) return v;
        try { return JSON.parse(v || '[]') || []; } catch (e) { return []; }
    };

    const handleEdit = (record) => {
        setEditingNode(record);
        form.resetFields();
        form.setFieldsValue({
            name: record.name,
            ip: record.ip,
            ipv4: record.ipv4 || '',
            ipv6: record.ipv6 || '',
            sync_port: record.sync_port,
            region: record.region || undefined,
            status: record.status === 'active',
            node_group_ids: record.node_groups ? record.node_groups.map(g => g.id) : [],
            is_l2: record.is_l2 || false,
            block_direct_access: record.block_direct_access || false,
            edge_type: record.edge_type || 'rust',
            internal_ip: record.internal_ip || '',
            vip: record.vip || '',
            is_accel: !!record.is_accel,
            rate_l1: Number(record.rate_l1 ?? 1),
            rate_l2: Number(record.rate_l2 ?? 1),
            rate_l3: Number(record.rate_l3 ?? 1),
            bgp_capable: !!record.bgp_capable,
            bgp_config: record.bgp_config || {},
            h3_exclude: parseH3Exclude(record.h3_exclude),
        });
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        form.resetFields();
        setEditingNode(null);
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            message.error('请填写所有必填项');
            return;
        }
        // 编辑时状态由列表里的启用/停用控制(带确认),表单不改 status
        if (editingNode) delete values.status;
        const response = editingNode
            ? await cdnService.updateNode({ ...values, id: editingNode.id })
            : await cdnService.createNode(values);

        if (response.success) {
            message.success(editingNode ? '已保存' : '已创建');
            setModalVisible(false);
            form.resetFields();
            if (!editingNode && response.data?.env) {
                setKeyModal({ node: { id: response.data.id, name: values.name }, env: response.data.env });
            }
            setEditingNode(null);
            loadNodes();
        } else {
            message.error(response.error || '操作失败');
        }
    };

    const handleDelete = async (record) => {
        const response = await cdnService.deleteNode(record.id);
        if (response.success) {
            message.success('已删除');
            loadNodes();
        } else {
            message.error(response.error || '删除失败');
        }
    };

    // ───────── 运维动作 ─────────
    const handleCheckHealth = async (record) => {
        setHealthMap(prev => ({ ...prev, [record.id]: { loading: true } }));
        const response = await cdnService.getNodeHealth(record.id);
        const d = response.success ? (response.data || {}) : { status: 'error', message: response.error };
        setHealthMap(prev => ({ ...prev, [record.id]: { loading: false, status: d.status, message: d.message } }));
        if (d.status === 'healthy') {
            message.success(`${record.name} 健康`);
        } else {
            message.error(`${record.name} 异常: ${d.message || '未知'}`);
        }
    };

    const handleToggleStatus = async (record) => {
        const nextActive = record.status !== 'active';
        setTogglingId(record.id);
        try {
            const response = await cdnService.updateNode({ id: record.id, status: nextActive });
            if (response.success) {
                message.success(nextActive ? '已启用' : '已停用');
                loadNodes();
            } else {
                message.error(response.error || '操作失败');
            }
        } finally {
            setTogglingId(null);
        }
    };

    const handleReload = async (record) => {
        setReloadingId(record.id);
        try {
            const response = await cdnNodeOpsService.reloadNode(record.id);
            if (response.success) {
                message.success('已下发重载指令');
            } else {
                message.error(response.error || '下发失败');
            }
        } finally {
            setReloadingId(null);
        }
    };

    const handleCopyEnv = async (node) => {
        const response = await cdnNodeOpsService.revealKey(node.id);
        if (!response.success) {
            message.error(response.error || '获取密钥失败');
            return;
        }
        const ok = await copyText(response.data?.env || '');
        ok ? message.success('edge.env 已复制') : message.error('复制失败');
    };

    const handleRotateKey = async () => {
        if (!keyModal?.node) return;
        setKeyModal(prev => ({ ...prev, rotating: true }));
        const response = await cdnNodeOpsService.rotateKey(keyModal.node.id);
        if (response.success) {
            message.success('密钥已轮换，请立即更新节点 edge.env');
            setKeyModal(prev => ({ ...prev, rotating: false, env: response.data?.env }));
            loadNodes();
        } else {
            message.error(response.error || '轮换失败');
            setKeyModal(prev => ({ ...prev, rotating: false }));
        }
    };

    const handleShowStatus = async (record) => {
        setStatusNode(record);
        setStatusModalVisible(true);
        setStatusLoading(true);
        try {
            const response = await cdnService.getNodeWsStatus(record.id);
            if (response.success) {
                setStatusData(response);
            } else {
                message.error(response.error || '获取状态失败');
            }
        } finally {
            setStatusLoading(false);
        }
    };

    const loadNodeVersions = async (nodeId, page = 1, pageSize = 20, status = null) => {
        setVersionsLoading(true);
        try {
            const response = await cdnService.getNodeSiteVersions(nodeId, page, pageSize, status);
            if (response.success) {
                setVersionsData(response.data);
            } else {
                message.error(response.error || '加载版本列表失败');
            }
        } finally {
            setVersionsLoading(false);
        }
    };

    const handleShowVersions = (record) => {
        setStatusNode(record);
        setVersionsFilter(null);
        setVersionsData(null);
        setVersionsModalVisible(true);
        loadNodeVersions(record.id, 1, 20, null);
    };

    // ───────── 表格 ─────────
    const versionsColumns = [
        {
            title: '域名', dataIndex: 'domain', key: 'domain', ellipsis: true,
            render: (text, r) => <span style={{ color: r.domain_status !== 'active' ? '#999' : undefined }}>{text}</span>
        },
        {
            title: '配置版本', key: 'config_version', width: 150,
            render: (_, r) => (
                <Space>
                    <span>{r.node_config_version}</span>
                    <span style={{ color: '#999' }}>/</span>
                    <span style={{ color: r.needs_config_update ? '#ff4d4f' : '#52c41a' }}>{r.latest_config_version ?? '-'}</span>
                    {r.needs_config_update && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                </Space>
            )
        },
        {
            title: 'SSL版本', key: 'ssl_version', width: 150,
            render: (_, r) => (
                <Space>
                    <span>{r.node_ssl_version}</span>
                    <span style={{ color: '#999' }}>/</span>
                    <span style={{ color: r.needs_ssl_update ? '#ff4d4f' : '#52c41a' }}>{r.latest_ssl_version ?? '-'}</span>
                    {r.needs_ssl_update && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                </Space>
            )
        },
        {
            title: '同步状态', dataIndex: 'sync_status', key: 'sync_status', width: 100,
            render: (s) => {
                const m = { synced: ['success', '已同步'], pending: ['processing', '等待中'], failed: ['error', '失败'] }[s] || ['default', s];
                return <Tag color={m[0]}>{m[1]}</Tag>;
            }
        },
        { title: '同步时间', dataIndex: 'synced_at', key: 'synced_at', width: 170, render: fmtTime }
    ];

    const columns = [
        {
            title: '节点', dataIndex: 'name', key: 'name', width: 260,
            sorter: (a, b) => String(a.name || a.hostname || '').localeCompare(String(b.name || b.hostname || '')),
            render: (text, r) => (
                <Space size={4} wrap>
                    <CloudServerOutlined />
                    <strong>{text || r.hostname}</strong>
                    <Tag color={r.edge_type === 'lua' ? 'default' : 'geekblue'}>{r.edge_type === 'lua' ? 'Lua' : 'Rust'}</Tag>
                    {r.is_l2 && <Tag color="blue">L2</Tag>}
                    {!!r.is_accel && <Tag color="purple">加速</Tag>}
                    {!!r.bgp_capable && <Tag>BGP</Tag>}
                    {parseH3Exclude(r.h3_exclude).length > 0 && <Tag title={parseH3Exclude(r.h3_exclude).join(', ')}>无 H3</Tag>}
                    {r.is_l2 && r.block_direct_access && <Tag color="orange">禁直连</Tag>}
                </Space>
            )
        },
        {
            title: 'IP', dataIndex: 'ip', key: 'ip', width: 170,
            render: (text, r) => (
                <Space direction="vertical" size={0}>
                    <Text copyable={{ text }}>{text}</Text>
                    {r.ipv6 && <Text type="secondary" style={{ fontSize: 12, maxWidth: 160 }} ellipsis={{ tooltip: r.ipv6 }}>{r.ipv6}</Text>}
                </Space>
            )
        },
        {
            title: '分组', dataIndex: 'node_groups', key: 'node_groups',
            render: (groups) => groups && groups.length > 0 ? (
                <Space size={[0, 4]} wrap>
                    {groups.map(g => <Tag key={g.id} icon={<ApartmentOutlined />} color="cyan">{g.name}</Tag>)}
                </Space>
            ) : <Text type="secondary">未分组</Text>
        },
        { title: '地区', dataIndex: 'region', key: 'region', render: (t, r) => <span><CountryFlag code={countryFromRegion(t)} />{t || '-'}{r.colo ? <Tag style={{ marginLeft: 6 }}>{r.colo}</Tag> : null}</span> },
        {
            title: '状态', dataIndex: 'status', key: 'status',
            render: (status) => {
                const m = {
                    active: { color: 'success', text: '启用' },
                    inactive: { color: 'default', text: '停用' },
                    maintenance: { color: 'warning', text: '维护' }
                }[status] || { color: 'default', text: status };
                return <Tag color={m.color}>{m.text}</Tag>;
            }
        },
        {
            title: '健康', key: 'health',
            render: (_, r) => {
                const h = r.health;
                if (!h) return <Text type="secondary">-</Text>;
                const tip = `连续成功 ${h.consecutive_ok} / 连续失败 ${h.consecutive_fail}` +
                    (h.last_error ? ` · ${h.last_error}` : '') + ` · 探测于 ${fmtTime(h.last_probe_at)}`;
                return (
                    <Tooltip title={tip}>
                        {h.dns_healthy
                            ? <Tag color="success">在 DNS</Tag>
                            : <Tag color="error">{h.consecutive_fail > 0 ? `失败 ${h.consecutive_fail}` : '未入 DNS'}</Tag>}
                    </Tooltip>
                );
            }
        },
        {
            title: 'BGP', key: 'bgp',
            render: (_, r) => {
                const b = r.bgp;
                if (!r.bgp_capable || !b) return <Text type="secondary">-</Text>;
                const m = BGP_META[b.status] || { color: 'default', text: b.status };
                return (
                    <Tooltip title={bgpTip(b)}>
                        <Space size={4}>
                            <Tag color={m.color}>{m.text}</Tag>
                            {b.config_synced === false && <Tag color="warning">配置未同步</Tag>}
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: '连接', key: 'ws',
            render: (_, r) => r.ws?.online
                ? <Tooltip title={`${r.ws.cdn_version || ''} · 心跳 ${fmtTime(r.ws.last_pong_at)}`}><Tag icon={<WifiOutlined />} color="success">在线</Tag></Tooltip>
                : <Tag icon={<DisconnectOutlined />} color={r.status === 'inactive' ? 'default' : 'error'}>离线</Tag>
        },
        {
            title: '24h 任务', key: 'tasks_24h',
            render: (_, r) => {
                const t = r.tasks_24h || {};
                return (
                    <Space size={4}>
                        <Text type="success">{t.acked || 0}</Text>
                        <Text type="secondary">/</Text>
                        <Text type={t.failed ? 'danger' : 'secondary'}>{t.failed || 0}</Text>
                        {t.pending > 0 && <Tag color="processing">{t.pending} 待</Tag>}
                    </Space>
                );
            }
        },
        {
            title: '最后在线', dataIndex: 'last_seen', key: 'last_seen', width: 170,
            sorter: (a, b) => (new Date(a.last_seen || 0)) - (new Date(b.last_seen || 0)),
            render: fmtTime,
        },
        {
            title: '操作', key: 'action', fixed: 'right', width: 200,
            render: (_, r) => {
                const health = healthMap[r.id];
                const isActive = r.status === 'active';
                return (
                    <Space size={0}>
                        <Tooltip title="探活">
                            <Button
                                type="link" size="small" loading={health?.loading}
                                icon={health && !health.loading
                                    ? (health.status === 'healthy'
                                        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />)
                                    : <WifiOutlined />}
                                onClick={() => handleCheckHealth(r)}
                            />
                        </Tooltip>
                        <Tooltip title="重载配置">
                            <Button
                                type="link" size="small" icon={<SyncOutlined />}
                                disabled={r.status === 'inactive'}
                                loading={reloadingId === r.id}
                                onClick={() => handleReload(r)}
                            />
                        </Tooltip>
                        <Tooltip title="编辑">
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
                        </Tooltip>
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    { key: 'status', icon: <CloudServerOutlined />, label: '状态', onClick: () => handleShowStatus(r) },
                                    { key: 'versions', icon: <DatabaseOutlined />, label: '站点版本', onClick: () => handleShowVersions(r) },
                                    { key: 'ips', icon: <GlobalOutlined />, label: 'IP', onClick: () => setIpsNode(r) },
                                    { key: 'key', icon: <KeyOutlined />, label: '密钥', onClick: () => setKeyModal({ node: r }) },
                                ],
                            }}
                        >
                            <Button type="link" size="small" icon={<MoreOutlined />} />
                        </Dropdown>
                        {isActive ? (
                            <Popconfirm
                                title="停用后该节点会从 DNS 中移除"
                                okText="停用" okButtonProps={{ danger: true }} cancelText="取消"
                                onConfirm={() => handleToggleStatus(r)}
                            >
                                <Tooltip title="停用">
                                    <Button type="link" size="small" danger icon={<DisconnectOutlined />} loading={togglingId === r.id} />
                                </Tooltip>
                            </Popconfirm>
                        ) : (
                            <Tooltip title="启用">
                                <Button type="link" size="small" icon={<CheckCircleOutlined />} loading={togglingId === r.id} onClick={() => handleToggleStatus(r)} />
                            </Tooltip>
                        )}
                        <Popconfirm
                            title="确定删除该节点？"
                            okText="删除" okButtonProps={{ danger: true }} cancelText="取消"
                            onConfirm={() => handleDelete(r)}
                        >
                            <Tooltip title="删除">
                                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    const tasks24h = statusData?.tasks_24h || {};
    const sh = statusData?.health;

    return (
        <div>
            <H3Suggestions open={h3Open} onClose={() => setH3Open(false)} onApplied={loadNodes} />
            <Card
                title={
                    <Space direction="vertical" size={0}>
                        <span>CDN 节点</span>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>发版通过 edge-handover 无损进行</Text>
                    </Space>
                }
                extra={
                    <Space wrap>
                        <Button onClick={() => setH3Open(true)}>H3 建议</Button>
                        <Button icon={<ReloadOutlined />} onClick={loadNodes} loading={loading}>刷新</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加节点</Button>
                    </Space>
                }
            >
                <Space wrap style={{ marginBottom: 16 }}>
                    <Input.Search
                        allowClear
                        placeholder="名称 / IP / 位置代码"
                        defaultValue={keyword}
                        style={{ width: 220 }}
                        onChange={(e) => { if (!e.target.value) setKeyword(''); }}
                        onSearch={(v) => setKeyword(v || '')}
                    />
                    <Select style={{ width: 140 }} value={selectedGroup} onChange={setSelectedGroup}>
                        <Option value="all">全部分组</Option>
                        <Option value="none">未分组</Option>
                        {nodeGroups.map(g => <Option key={g.id} value={String(g.id)}>{g.name}</Option>)}
                    </Select>
                    <Select style={{ width: 120 }} value={selectedRegion} onChange={setSelectedRegion}>
                        <Option value="all">全部地区</Option>
                        {regions.map(r => <Option key={r} value={r}><CountryFlag code={countryFromRegion(r)} />{r}</Option>)}
                    </Select>
                    <Select style={{ width: 110 }} value={wsFilter} onChange={setWsFilter} options={[
                        { value: 'all', label: '全部连接' },
                        { value: 'online', label: '在线' },
                        { value: 'offline', label: '离线' },
                    ]} />
                    <Space size={4}>
                        <Switch size="small" checked={showInactive} onChange={setShowInactive} />
                        <Text type="secondary">显示停用节点{inactiveCount ? ` (${inactiveCount})` : ''}</Text>
                    </Space>
                </Space>
                {loadError && (
                    <Alert
                        type="error" showIcon style={{ marginBottom: 12 }}
                        message={loadError}
                        action={<Button size="small" onClick={loadNodes}>重试</Button>}
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={filteredNodes}
                    rowKey="id"
                    loading={loading}
                    sticky
                    pagination={{ position: ['topRight', 'bottomRight'], defaultPageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 个节点` }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="暂无节点" /> }}
                />
            </Card>

            <Modal
                title={<Space><GlobalOutlined />{ipsNode?.name || ipsNode?.hostname} · IP</Space>}
                open={!!ipsNode}
                onCancel={() => setIpsNode(null)}
                footer={null}
                width={760}
                destroyOnClose
            >
                {ipsNode && <NodeIpsPanel nodeId={ipsNode.id} />}
            </Modal>

            {/* 新建 / 编辑 */}
            <Modal
                title={editingNode ? '编辑节点' : '添加节点'}
                open={modalVisible}
                onCancel={handleCancel}
                onOk={handleSubmit}
                okText="保存"
                cancelText="取消"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="节点名称" name="name" rules={[{ required: true, message: '请输入节点名称' }]} tooltip="即边缘 edge.env 的 NODE_ID">
                        <Input placeholder="HK-01" />
                    </Form.Item>
                    <Form.Item label="IP" name="ip" rules={[{ required: true, message: '请输入 IP' }]}>
                        <Input placeholder="203.0.113.10" />
                    </Form.Item>
                    <Space style={{ display: 'flex' }} align="start">
                        <Form.Item
                            label="DNS IPv4" name="ipv4" style={{ flex: 1 }}
                            rules={[{ pattern: /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/, message: 'IPv4 无效' }]}
                        >
                            <Input placeholder="留空用上方 IP" />
                        </Form.Item>
                        <Form.Item label="DNS IPv6" name="ipv6" style={{ flex: 1 }}>
                            <Input placeholder="可选" />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }} align="start">
                        <Form.Item label="内网 IP" name="internal_ip" style={{ flex: 1 }} tooltip="同集群节点之间中转时优先使用">
                            <Input placeholder="可选，如 10.0.0.2" />
                        </Form.Item>
                        <Form.Item label="VIP" name="vip" style={{ flex: 1 }} tooltip="同机房多台节点共享的浮动 IP">
                            <Input placeholder="可选" />
                        </Form.Item>
                    </Space>
                    <Form.Item label="位置代码" name="colo" tooltip="访客在 /cdn-cgi/trace 看到的 colo,如 FRA、LAX、HKG;留空显示 UNK" normalize={(v) => (v || '').toUpperCase()}
                        rules={[{ pattern: /^[A-Za-z0-9]{2,8}$/, message: '2-8 位字母或数字' }]}>
                        <Input placeholder="如 FRA / LAX / HKG" maxLength={8} style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item label="地区" name="region">
                        <AutoComplete
                            allowClear
                            placeholder="如 eu / ap / de"
                            options={regions.map(r => ({ value: r }))}
                            filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item label="节点分组" name="node_group_ids">
                        <Select mode="multiple" placeholder="可多选" allowClear>
                            {nodeGroups.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                        </Select>
                    </Form.Item>
                    <Space size="large">
                        <Form.Item label="集群缓存层 (L2)" name="is_l2" valuePropName="checked" tooltip="同集群的边缘节点优先经它回源并缓存">
                            <Switch />
                        </Form.Item>
                        <Form.Item label="加速节点 (L3)" name="is_accel" valuePropName="checked" tooltip="域名开启回源加速后，可经它就近回源">
                            <Switch />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(p, c) => p.is_l2 !== c.is_l2}>
                            {({ getFieldValue }) => getFieldValue('is_l2') ? (
                                <Form.Item label="仅接受 L1 回源" name="block_direct_access" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                            ) : null}
                        </Form.Item>
                        {!editingNode && (
                            <Form.Item label="创建后启用" name="status" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        )}
                    </Space>
                    <Form.Item label="不提供 HTTP/3" name="h3_exclude" tooltip="这些访客到本节点 UDP 不通时选；即使域名开启 HTTP/3 也只用 HTTP/2。可填其他国家代码">
                        <Select mode="tags" allowClear placeholder="不限制" tokenSeparators={[',', ' ']} options={[
                            { value: '*', label: '全部访客' },
                            { value: 'CN', label: '中国大陆' },
                            { value: 'CN:telecom', label: '中国电信' },
                            { value: 'CN:unicom', label: '中国联通' },
                            { value: 'CN:mobile', label: '中国移动' },
                        ]} />
                    </Form.Item>
                    <Collapse
                        ghost
                        items={[{
                            key: 'billing',
                            label: '计费倍率',
                            forceRender: true,
                            children: (
                                <Space style={{ display: 'flex' }} align="start">
                                    <Form.Item label="作为 L1" name="rate_l1" style={{ flex: 1 }} tooltip="该节点直接服务访客时的流量倍率">
                                        <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item label="作为 L2" name="rate_l2" style={{ flex: 1 }} tooltip="为其他节点做集群缓存层时；走内网可设 0">
                                        <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item label="作为 L3" name="rate_l3" style={{ flex: 1 }} tooltip="作为加速节点回源时">
                                        <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Space>
                            ),
                        }, {
                            key: 'bgp',
                            label: 'BGP / Anycast',
                            forceRender: true,
                            children: (
                                <>
                                    <Form.Item label="支持 BGP（BIRD）" name="bgp_capable" valuePropName="checked" tooltip="参与所在集群的 anycast 宣告">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(p, c) => p.bgp_capable !== c.bgp_capable}>
                                        {({ getFieldValue }) => getFieldValue('bgp_capable') ? (
                                            <>
                                                <Space style={{ display: 'flex' }} align="start">
                                                    <Form.Item label="本端 ASN" name={['bgp_config', 'local_asn']} style={{ flex: 1 }}>
                                                        <InputNumber min={1} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                    <Form.Item label="对端 ASN" name={['bgp_config', 'peer_asn']} style={{ flex: 1 }}>
                                                        <InputNumber min={1} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                    <Form.Item label="Multihop" name={['bgp_config', 'multihop']} style={{ width: 100 }}>
                                                        <InputNumber min={0} max={255} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Space>
                                                <Space style={{ display: 'flex' }} align="start">
                                                    <Form.Item label="邻居 IPv4" name={['bgp_config', 'neighbor']} style={{ flex: 1 }}>
                                                        <Input placeholder="上游提供的 BGP 邻居" />
                                                    </Form.Item>
                                                    <Form.Item label="邻居 IPv6" name={['bgp_config', 'neighbor6']} style={{ flex: 1 }}>
                                                        <Input placeholder="可选" />
                                                    </Form.Item>
                                                </Space>
                                                <Form.Item label="会话密码" name={['bgp_config', 'password']}>
                                                    <Input.Password placeholder="可选" autoComplete="new-password" />
                                                </Form.Item>
                                            </>
                                        ) : null}
                                    </Form.Item>
                                </>
                            ),
                        }, {
                            key: 'adv',
                            label: '高级',
                            forceRender: true,
                            children: (
                                <>
                                    <Form.Item label="边缘类型" name="edge_type">
                                        <Select>
                                            <Option value="rust">Rust</Option>
                                            <Option value="lua">Lua（已退役）</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="同步端口" name="sync_port" rules={[{ required: true, message: '请输入端口' }]}>
                                        <Input type="number" />
                                    </Form.Item>
                                </>
                            )
                        }]}
                    />
                </Form>
            </Modal>

            {/* 密钥 */}
            <Modal
                title={<Space><KeyOutlined />节点密钥 - {keyModal?.node?.name}</Space>}
                open={!!keyModal}
                onCancel={() => setKeyModal(null)}
                footer={null}
                destroyOnClose
            >
                {keyModal && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {keyModal.env ? (
                            <>
                                <Alert type="warning" showIcon message="写入节点 /opt/edge/edge.env 后重启 edge-node" />
                                <Paragraph copyable={{ text: keyModal.env }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{keyModal.env}</pre>
                                </Paragraph>
                            </>
                        ) : (
                            <Space>
                                <Text code>{keyModal.node.sync_key_masked || '********'}</Text>
                                <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyEnv(keyModal.node)}>复制 edge.env</Button>
                            </Space>
                        )}
                        <Divider style={{ margin: '8px 0' }} />
                        <Popconfirm
                            title="轮换密钥"
                            description="须同时更新节点 /opt/edge/edge.env 的 NODE_SECRET 并重启，否则节点立即失联"
                            okText="轮换" okButtonProps={{ danger: true }} cancelText="取消"
                            onConfirm={handleRotateKey}
                        >
                            <Button danger size="small" loading={keyModal.rotating}>轮换密钥</Button>
                        </Popconfirm>
                    </Space>
                )}
            </Modal>

            {/* 站点版本 */}
            <Modal
                title={<Space><DatabaseOutlined />站点版本 - {statusNode?.name}</Space>}
                open={versionsModalVisible}
                onCancel={() => setVersionsModalVisible(false)}
                footer={null}
                width={900}
            >
                {versionsLoading && !versionsData && (
                    <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
                )}
                {versionsData && (
                    <>
                        <Space style={{ marginBottom: 16 }} wrap>
                            <Tag>总计 {versionsData.stats?.total || 0}</Tag>
                            <Tag color="success">已同步 {versionsData.stats?.synced || 0}</Tag>
                            <Tag color="processing">等待 {versionsData.stats?.pending || 0}</Tag>
                            <Tag color="error">失败 {versionsData.stats?.failed || 0}</Tag>
                            {versionsData.stats?.needs_update > 0 && <Tag color="warning">需更新 {versionsData.stats.needs_update}</Tag>}
                            <Select
                                style={{ width: 120 }} placeholder="筛选状态" allowClear value={versionsFilter}
                                onChange={(s) => { setVersionsFilter(s); loadNodeVersions(statusNode.id, 1, 20, s); }}
                            >
                                <Option value="synced">已同步</Option>
                                <Option value="pending">等待中</Option>
                                <Option value="failed">失败</Option>
                            </Select>
                        </Space>
                        <Table
                            columns={versionsColumns}
                            dataSource={versionsData.items || []}
                            rowKey="id"
                            loading={versionsLoading}
                            size="small"
                            pagination={{
                                current: versionsData.pagination?.page || 1,
                                pageSize: versionsData.pagination?.page_size || 20,
                                total: versionsData.pagination?.total || 0,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 条`,
                                onChange: (page, pageSize) => loadNodeVersions(statusNode.id, page, pageSize, versionsFilter)
                            }}
                        />
                    </>
                )}
            </Modal>

            {/* 节点状态 */}
            <Modal
                title={<Space><CloudServerOutlined />节点状态 - {statusNode?.name}</Space>}
                open={statusModalVisible}
                onCancel={() => { setStatusModalVisible(false); setStatusData(null); }}
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} loading={statusLoading} onClick={() => statusNode && handleShowStatus(statusNode)}>刷新</Button>,
                    <Button key="close" onClick={() => { setStatusModalVisible(false); setStatusData(null); }}>关闭</Button>
                ]}
                width={640}
            >
                {statusLoading && !statusData ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
                ) : statusData ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Descriptions size="small" bordered column={2} title="连接">
                            <Descriptions.Item label="WS">
                                {statusData.connection?.status === 'connected' ? <Tag color="success">已连接</Tag> : <Tag>未连接</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="版本">{statusData.connection?.cdn_version || '-'}</Descriptions.Item>
                            <Descriptions.Item label="来源 IP">{statusData.connection?.client_ip || '-'}</Descriptions.Item>
                            <Descriptions.Item label="最后心跳">{fmtTime(statusData.connection?.last_pong_at)}</Descriptions.Item>
                            <Descriptions.Item label="最后在线" span={2}>{fmtTime(statusData.node?.last_seen)}</Descriptions.Item>
                        </Descriptions>
                        <Descriptions size="small" bordered column={2} title="健康探测">
                            {sh ? (
                                <>
                                    <Descriptions.Item label="DNS">{sh.dns_healthy ? <Tag color="success">在 DNS</Tag> : <Tag color="error">已摘除</Tag>}</Descriptions.Item>
                                    <Descriptions.Item label="80 / 443">
                                        {sh.last_http_code ?? '-'} / {sh.last_https_code ?? '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="连续成功">{sh.consecutive_ok}</Descriptions.Item>
                                    <Descriptions.Item label="连续失败">{sh.consecutive_fail}</Descriptions.Item>
                                    <Descriptions.Item label="最近探测">{fmtTime(sh.last_probe_at)}</Descriptions.Item>
                                    <Descriptions.Item label="错误">{sh.last_error || '-'}</Descriptions.Item>
                                </>
                            ) : (
                                <Descriptions.Item label="状态">暂无探测记录</Descriptions.Item>
                            )}
                        </Descriptions>
                        {statusData.bgp && (() => {
                            const b = statusData.bgp;
                            const m = BGP_META[b.status] || { color: 'default', text: b.status };
                            return (
                                <>
                                    <Descriptions size="small" bordered column={2} title="BGP">
                                        <Descriptions.Item label="状态"><Tag color={m.color}>{m.text}</Tag></Descriptions.Item>
                                        <Descriptions.Item label="边缘健康">{b.status === 'no_report' ? '-' : (b.healthy ? <Tag color="success">正常</Tag> : <Tag color="error">异常</Tag>)}</Descriptions.Item>
                                        <Descriptions.Item label="配置版本">
                                            {b.version || '-'}
                                            {b.config_synced === false && <Tag color="warning" style={{ marginLeft: 6 }}>应为 {b.expected_version}</Tag>}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="上报时间">{b.reported_at ? fmtTime(b.reported_at * 1000) : '-'}</Descriptions.Item>
                                    </Descriptions>
                                    {(b.sessions || []).length > 0 && (
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey="name"
                                            dataSource={b.sessions}
                                            columns={[
                                                { title: '协议', dataIndex: 'name', width: 110 },
                                                { title: '状态', key: 'st', width: 130, render: (_, x) => x.proto === 'BGP'
                                                    ? <Tag color={(x.info || '').startsWith('Established') ? 'success' : 'error'}>{x.info || x.state}</Tag>
                                                    : <Tag color={x.state === 'up' ? 'success' : 'warning'}>{x.state === 'up' ? '宣告' : '撤回'}</Tag> },
                                                { title: '导出', dataIndex: 'exported', width: 60, render: (v, x) => x.proto === 'BGP' ? v : '-' },
                                                { title: '自', dataIndex: 'since' },
                                            ]}
                                        />
                                    )}
                                    {(b.events || []).length > 0 && (
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey={(e, i) => `${e.at}-${i}`}
                                            dataSource={b.events}
                                            columns={[
                                                { title: '时间', dataIndex: 'at', width: 160, render: (t) => fmtTime(t * 1000) },
                                                { title: '事件', dataIndex: 'msg' },
                                            ]}
                                        />
                                    )}
                                </>
                            );
                        })()}
                        <Descriptions size="small" bordered column={3} title="近 24h 任务">
                            <Descriptions.Item label="已确认">{tasks24h.acked || 0}</Descriptions.Item>
                            <Descriptions.Item label="失败">{tasks24h.failed || 0}</Descriptions.Item>
                            <Descriptions.Item label="进行中">{(tasks24h.pending || 0) + (tasks24h.dispatched || 0) + (tasks24h.delivered || 0)}</Descriptions.Item>
                        </Descriptions>
                        {Array.isArray(statusData.recent_failed) && statusData.recent_failed.length > 0 && (
                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(r, i) => `${r.task_type}-${r.failed_at}-${i}`}
                                dataSource={statusData.recent_failed}
                                columns={[
                                    { title: '类型', dataIndex: 'task_type', width: 120 },
                                    { title: '错误', dataIndex: 'last_error', ellipsis: true },
                                    { title: '时间', dataIndex: 'failed_at', width: 170, render: fmtTime }
                                ]}
                            />
                        )}
                    </Space>
                ) : null}
            </Modal>
        </div>
    );
};

export default Nodes;
